import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { Button, ButtonLink, Chip, EmptyState, Field, FormError, PageTitle, Spinner } from '../components/ui'
import { cx } from '../lib/cx'
import { api, call, messageOf } from '../lib/api'
import { categoryVoteQuery } from '../lib/queries'
import type { components } from '../lib/api.gen'

type Vote = components['schemas']['CategoryVoteView']
type Candidate = components['schemas']['CategoryCandidateView']

// The ballot for next week's categories (designs/20261027-category-vote.md
// §3, §6): pick up to two, suggest one of your own, see the running counts.
// The two with the most picks win; the third is always Wild Card.
export const Route = createFileRoute('/l/$slug/next-week')({ component: NextWeek })

function closesText(v: Vote) {
  // Stored as the following midnight; people read it as the evening before.
  const at = new Date(new Date(v.closes_at).getTime() - 60_000)
  return at.toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' })
}

function NextWeek() {
  const { slug } = Route.useParams()
  const q = useQuery(categoryVoteQuery(slug))
  if (q.isPending) return <div className="grid place-items-center py-16 text-muted"><Spinner /></div>
  if (q.isError) return <EmptyState title="Couldn't load the vote">{messageOf(q.error)}</EmptyState>
  if (!q.data)
    return (
      <EmptyState title="No vote right now" action={<ButtonLink to="/l/$slug" params={{ slug }}>Back to this week</ButtonLink>}>
        The vote on next week's categories opens Wednesday morning.
      </EmptyState>
    )
  return <Ballot slug={slug} vote={q.data} />
}

export function Ballot({ slug, vote: v }: { slug: string; vote: Vote }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string>()
  const set = (next: Vote) => {
    setError(undefined)
    qc.setQueryData(categoryVoteQuery(slug).queryKey, next)
  }
  const toggle = useMutation({
    mutationFn: (c: Candidate) =>
      call(
        c.picked
          ? api.DELETE('/leagues/{slug}/category-vote/picks/{id}', { params: { path: { slug, id: c.id } } })
          : api.PUT('/leagues/{slug}/category-vote/picks/{id}', { params: { path: { slug, id: c.id } } }),
      ),
    onSuccess: set,
    onError: (e) => setError(messageOf(e)),
  })
  const remove = useMutation({
    mutationFn: (c: Candidate) => call(api.DELETE('/leagues/{slug}/category-vote/candidates/{id}', { params: { path: { slug, id: c.id } } })),
    onSuccess: set,
    onError: (e) => setError(messageOf(e)),
  })
  const candidates = v.candidates ?? []
  const full = v.my_picks >= v.max_picks
  const left = v.max_picks - v.my_picks
  return (
    <>
      <PageTitle eyebrow={`Round ${v.round_number}`} aside={<Chip tone={v.open ? 'live' : 'done'}>{v.open ? 'Voting' : 'Closed'}</Chip>}>
        Next week's categories
      </PageTitle>
      <p className="mb-4 text-[14.5px] text-muted">
        Pick up to {v.max_picks}. The two with the most picks win, and the third is always <b className="font-semibold text-ink">{v.wild_card}</b>.
        {v.open && ` Voting closes ${closesText(v)}.`}
      </p>
      <FormError>{error}</FormError>
      <ul className="mt-3 grid gap-2" aria-label="Ballot">
        {candidates.map((c) => (
          <li key={c.id} className={cx('flex items-center gap-3 rounded-card border bg-surface px-4 py-3', c.picked ? 'border-ink' : 'border-line')}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[16px] font-semibold">{c.name}</p>
              {c.suggested_by_name && <p className="truncate text-[13px] text-muted">Suggested by {c.mine ? 'you' : c.suggested_by_name}</p>}
            </div>
            <span className="font-score text-[22px] font-bold" aria-label={`${c.picks} ${c.picks === 1 ? 'pick' : 'picks'}`}>{c.picks}</span>
            {v.open && (
              <Button
                size="sm"
                variant={c.picked ? 'ink' : 'quiet'}
                aria-pressed={c.picked}
                disabled={!c.picked && full}
                busy={toggle.isPending && toggle.variables?.id === c.id}
                onClick={() => toggle.mutate(c)}
              >
                {c.picked ? 'Picked' : 'Pick'}
              </Button>
            )}
            {v.open && v.is_admin && c.source === 'member' && (
              <Button size="sm" variant="danger" busy={remove.isPending && remove.variables?.id === c.id} onClick={() => remove.mutate(c)} aria-label={`Remove ${c.name}`}>
                Remove
              </Button>
            )}
          </li>
        ))}
      </ul>
      {v.open && (
        <p className="mt-3 text-[13px] text-muted">{full ? 'You’ve used both picks. Tap one to take it back.' : `${left} ${left === 1 ? 'pick' : 'picks'} left.`}</p>
      )}
      {v.can_suggest && <Suggest slug={slug} onDone={set} />}
    </>
  )
}

function Suggest({ slug, onDone }: { slug: string; onDone: (v: Vote) => void }) {
  const [name, setName] = useState('')
  const add = useMutation({
    mutationFn: (n: string) => call(api.POST('/leagues/{slug}/category-vote/suggestions', { params: { path: { slug } }, body: { name: n } })),
    onSuccess: (v) => {
      setName('')
      onDone(v)
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim()) add.mutate(name.trim())
  }
  return (
    <form onSubmit={submit} className="mt-6 grid gap-3 rounded-card border border-line bg-surface p-4">
      <Field
        label="Suggest a category"
        help="One a week. It goes on the ballot for everyone."
        error={add.isError ? messageOf(add.error) : undefined}
        value={name}
        maxLength={40}
        onChange={(e) => setName(e.target.value)}
        placeholder="Mid-yawn"
      />
      <Button type="submit" busy={add.isPending} disabled={!name.trim()}>
        Add to the ballot
      </Button>
    </form>
  )
}
